<template lang="html">
  <section v-if="article && user" :style="themableStyle" class="theme-color">
    <div class="container">
      <section class="hero is-white is-small has-bg-img">
        <!-- Hero body -->
        <div class="hero-body">
          <div class="columns">
            <!-- Hero Left -->
            <div class="column is-1 is-paddingless pt-3">
              <router-link :to="{ path: `/users/${user.username}` }">
                <figure class="image avatar">
                  <img style="border-radius: 50%" :src="user.avatar.url" />
                </figure>
              </router-link>
            </div>

            <div class="column is-paddingless pt-3 pl-3">
              <p class="has-text-weight-semibold is-size-3">
                {{ article.title }}
                <b-tooltip
                  append-to-body
                  position="is-right"
                  :label="article.article_topic.name"
                >
                  <article-topic-icon :article-topic="article.article_topic.name" />
                </b-tooltip>
                <b-tooltip
                  v-if="article.hidden"
                  append-to-body
                  position="is-right"
                  label="Private"
                  type="is-warning"
                >
                  <b-icon icon="lock"></b-icon>
                </b-tooltip>
                <b-tooltip
                  v-if="!article.hidden"
                  append-to-body
                  position="is-right"
                  label="Public"
                  type="is-success"
                >
                  <b-icon icon="earth"></b-icon>
                </b-tooltip>
              </p>
              <p class="is-size-5">{{ article.subtitle }}</p>
              <p class="is-size-6">
                by
                <router-link aria-current="page" :to="{ path: `/users/${user.username}` }"
                  ><span class="has-text-dark-orange has-text-weight-semibold">{{
                    user.cosplayer_name
                  }}</span></router-link
                >
              </p>
              <p class="is-size-7 is-italic is-bottom">
                Created {{ dateFromNow(article.created_at) }} - Last update
                {{ dateFromNow(article.updated_at) }}
              </p>
            </div>
            <!-- Hero right side -->
            <div v-if="currentUser" class="column is-narrow">
              <!-- Edit button -->
              <span v-if="owner" class="p-1">
                <b-tooltip
                  label="Edit article"
                  type="is-warning is-inverted"
                  position="is-left"
                >
                  <b-button
                    class="is-warning is-inverted"
                    @click="isUpdateArticleModalActive = true"
                  >
                    <b-icon icon="pencil"></b-icon> </b-button
                ></b-tooltip>
              </span>

              <!-- Add teacher button -->
              <add-teacher-button
                v-if="!owner"
                :key="user.username"
                :teacher="user"
                :user="currentUser.attributes.username"
              />

              <!-- Change theme -->
              <theme-changer
                v-if="owner"
                :theme="article.theme"
                :themable-id="article.id"
                :themable-type="'article'"
                @themeUpdated="article.theme.name = $event"
              />

              <!-- Manage images -->
              <image-management
                v-if="owner"
                :imageable-id="article.id"
                :imageable-type="'article'"
                @imagesUpdated="images = $event"
              />

              <!-- Add to collection button -->
              <add-to-collection
                :collectableType="'article'"
                :collectableId="article.id"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Picture -->
      <carousel-horizontal :images="images" />

      <section class="section">
        <div class="container">
          <nav class="breadcrumb" aria-label="breadcrumbs">
            <ul>
              <li>
                <router-link
                  aria-current="page"
                  :to="{ path: `/users/${user.username}` }"
                  >{{ user.cosplayer_name }}</router-link
                >
              </li>
              <li class="is-active">
                <a href="#" aria-current="page">{{ article.title }}</a>
              </li>
            </ul>
          </nav>
          <section v-if="article.video" class="hero is-light-blue is-small has-bg-img">
            <div class="hero-body">
              <div class="container">
                <div class="columns is-centered">
                  <div class="column is-two-thirds">
                    <h1 class="title is-size-4 has-text-gray">
                      Video <b-icon icon="youtube"></b-icon>
                    </h1>
                    <div>
                      <figure class="image is-16by9">
                        <iframe
                          class="has-ratio"
                          width="640"
                          height="360"
                          :src="`https://www.youtube.com/embed/${article.video}`"
                          frameborder="0"
                          allowfullscreen
                        ></iframe>
                      </figure>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Starting word-->
          <div
            v-if="article.starting_word"
            class="has-text-centered has-text-weight-semibold is-size-4 has-text-dark-orange is-italic p-3"
          >
            {{ article.starting_word }}
            <a v-if="owner && editing" @click="isUpdateArticleModalActive = true"
              ><b-icon type="is-success" icon="pencil"></b-icon
            ></a>
          </div>
          <div
            v-else-if="owner && editing"
            class="has-text-centered is-size-5 has-text-gray p-3"
          >
            Add a starting word
            <a @click="isUpdateArticleModalActive = true"
              ><b-icon type="is-success" icon="plus-circle-outline"></b-icon
            ></a>
          </div>

          <!-- Steps -->
          <steps
            owner="owner"
            :steppable-id="article.id"
            :steppable-type="'article'"
            :editing="editing"
            @editingUpdated="editing = $event"
          />

          <!-- Ending word-->
          <div
            v-if="article.ending_word"
            class="has-text-centered has-text-weight-semibold is-size-4 has-text-dark-orange is-italic p-3"
          >
            {{ article.ending_word }}
            <a v-if="owner && editing" @click="isUpdateArticleModalActive = true"
              ><b-icon type="is-success" icon="pencil"></b-icon
            ></a>
          </div>
          <div
            v-else-if="owner && editing"
            class="has-text-centered is-size-5 has-text-gray p-3"
          >
            Add an ending word
            <a @click="isUpdateArticleModalActive = true"
              ><b-icon type="is-success" icon="plus-circle-outline"></b-icon
            ></a>
          </div>

          <div v-if="owner" class="buttons is-centered p-5">
            <b-tooltip type="is-danger" label="Not yet implemented" position="is-top">
              <b-button
                @click="isPublishModalActive = true"
                class="is-success"
                size="is-medium"
                icon-left="page-next-outline"
                rounded
                disabled
              >
                Publish updates
              </b-button>
            </b-tooltip>
          </div>

          <div v-if="article.tag_list.length" class="columns is-centered">
            <div class="column is-narrow">
              <attribute-title :title="'Tags'" />
              <tag-list :tags="article.tag_list" :slice-index="3" />
            </div>
          </div>
        </div>
      </section>

      <!-- Modals and Loaders -->

      <b-modal
        :active.sync="isUpdateArticleModalActive"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-modal
      >
        <update-modal @updatedArticle="updateArticle" :article="article" />
      </b-modal>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import UpdateModal from './UpdateModal'
import ThemeChanger from '@/components/buttons/ThemeChanger'
import { dateMixin } from '@/mixins/dateMixin.js'
import { backendMixin } from '@/mixins/backendMixin.js'
import { themableMixin } from '@/mixins/themableMixin'
import AddToCollection from '@/components/buttons/AddToCollection'
import Steps from '@/components/concerns/Steps/Steps'
import AddTeacherButton from '@/components/buttons/AddTeacherButton'
import ArticleTopicIcon from '@/views/Article/ArticleTopicIcon'
import ImageManagement from '@/components/concerns/Images/ImageManagement'
import CarouselHorizontal from '@/components/concerns/Images/CarouselHorizontal'
import TagList from '@/components/concerns/Tags/TagList'
import AttributeTitle from '@/components/Attributes/AttributeTitle'

export default {
  mixins: [dateMixin, backendMixin, themableMixin],
  data () {
    return {
      article: null,
      user: null,
      editing: false,
      isUpdateArticleModalActive: false,
      images: []
    }
  },
  components: {
    CarouselHorizontal,
    ImageManagement,
    ArticleTopicIcon,
    UpdateModal,
    ThemeChanger,
    AddToCollection,
    AddTeacherButton,
    Steps,
    TagList,
    AttributeTitle
  },
  computed: {
    ...mapGetters({
      isOwner: 'isOwner',
      currentUser: 'currentUser',
      isLoggedIn: 'isOwner'
    }),
    owner () {
      return this.isOwner(this.article.user_id)
    },
    progressType () {
      if (this.article.progress === 10) return 'is-success'
      else return 'is-primary'
    },
    themableTheme () {
      return this.article ? this.article.theme.name : null
    }
  },
  created () {
    this.fetchArticle()
  },
  watch: {
    $route: 'fetchArticle'
  },
  methods: {
    fetchArticle () {
      this.$store
        .dispatch('articles/show', this.$route.params.articleId)
        .then((resp) => {
          this.article = resp.data.data.attributes
          this.$store
            .dispatch('users/show', this.article.user_username)
            .then((resp) => {
              this.user = resp.data.data.attributes
            })
            .catch((_) => {
              // TODO: Handle error
              console.log('Error on article user')
            })
          this.fetchImages()
        })
        .catch((error) => {
          this.handleError(error, this.$route)
        })
    },
    fetchImages () {
      this.$store
        .dispatch('images/index', {
          imageableType: 'article',
          imageableId: this.$route.params.articleId
        })
        .then((resp) => {
          this.images = resp.data.data
        })
        .catch((error) => {
          console.log('error')
          console.log(error.response)
        })
    },
    updateArticle (newAttributes) {
      this.article = newAttributes
    }
  }
}
</script>

<style lang="scss" scoped></style>
